"use client";

import { Check, X } from "lucide-react";

interface PasswordStrengthIndicatorProps {
  passwordValue: string;
  showAvoidTip?: boolean;
  showPrivacyNote?: boolean;
}

export function PasswordStrengthIndicator({
  passwordValue,
  showAvoidTip = false,
  showPrivacyNote = false,
}: PasswordStrengthIndicatorProps) {
  const validations = {
    minLength: passwordValue.length >= 8,
    hasUppercase: /[A-Z]/.test(passwordValue),
    hasLowercase: /[a-z]/.test(passwordValue),
    hasNumber: /\d/.test(passwordValue),
    hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(passwordValue),
  };

  if (passwordValue.length === 0) {
    return null;
  }

  return (
    <div
      className="mt-3 overflow-hidden rounded-xl border border-[#e9e7f4] bg-primary-faint px-4 py-3.5 animate-in fade-in-0 slide-in-from-top-2 duration-200"
      role="region"
      aria-label="Password suggestions"
    >
      <h4 className="mb-2 text-[13px] font-semibold text-foreground">
        Password suggestions
      </h4>
      <ul className="space-y-1.5 text-[13px]">
        <li className="flex items-center space-x-2">
          {validations.minLength ? (
            <Check className="h-4 w-4 text-green-600 shrink-0" />
          ) : (
            <X className="h-4 w-4 text-[#a7b0b5] shrink-0" />
          )}
          <span
            className={
              validations.minLength ? "text-green-700" : "text-muted-foreground"
            }
          >
            At least 8 characters long
          </span>
        </li>
        <li className="flex items-center space-x-2">
          {validations.hasUppercase && validations.hasLowercase ? (
            <Check className="h-4 w-4 text-green-600 shrink-0" />
          ) : (
            <X className="h-4 w-4 text-[#a7b0b5] shrink-0" />
          )}
          <span
            className={
              validations.hasUppercase && validations.hasLowercase
                ? "text-green-700"
                : "text-muted-foreground"
            }
          >
            Include uppercase and lowercase letters
          </span>
        </li>
        <li className="flex items-center space-x-2">
          {validations.hasNumber ? (
            <Check className="h-4 w-4 text-green-600 shrink-0" />
          ) : (
            <X className="h-4 w-4 text-[#a7b0b5] shrink-0" />
          )}
          <span
            className={
              validations.hasNumber ? "text-green-700" : "text-muted-foreground"
            }
          >
            Include at least one number
          </span>
        </li>
        <li className="flex items-center space-x-2">
          {validations.hasSpecialChar ? (
            <Check className="h-4 w-4 text-green-600 shrink-0" />
          ) : (
            <X className="h-4 w-4 text-[#a7b0b5] shrink-0" />
          )}
          <span
            className={
              validations.hasSpecialChar
                ? "text-green-700"
                : "text-muted-foreground"
            }
          >
            {"Include a special character (!@#$%^&*)"}
          </span>
        </li>
        {showAvoidTip && (
          <li className="flex items-center space-x-2">
            <X className="h-4 w-4 text-[#a7b0b5] shrink-0" />
            <span className="text-muted-foreground">
              Avoid common passwords or personal information
            </span>
          </li>
        )}
      </ul>
      {showPrivacyNote && (
        <p className="text-xs text-gray-500 mt-3 italic">
          Strong passwords help protect sensitive healthcare data and maintain
          patient privacy.
        </p>
      )}
    </div>
  );
}
